import { useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
} from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

import { ThemeProvider } from 'styled-components';
import { dark, light } from 'themes';
import { load as loadFont } from 'webfontloader';

import { CAPTCHA_KEY, LocalStorageKeys, outletRoutes, routes } from 'commons';
import { ThemeContext } from 'contexts';
import { setLocalStorageItem } from 'helpers';
import { useAuth, useClarity } from 'hooks';
import {
  BatchCollectionPage,
  ConfirmCollectOutlet,
  ConsignmentDetails,
  DashboardPage,
  DiscountsPage,
  ExchangeAndReturnPage,
  ExchangeReturnForm,
  FinancialTransactionsPage,
  LoginPage,
  NewExchange,
  NotFoundPage,
  OpeningDaysPage,
  OrdersCodeListPage,
  PerformancePage,
  PickingManagement,
  PixTransactionsReportPage,
  ProfilePage,
  RegulatoryInformationPage,
  ResetPasswordPage,
  ReturnDetails,
  ReturnsAndRefundsDetails,
  ReturnsAndRefundsPage,
  ScoreReportPage,
  SelectConsignmentsOutlet,
  StepCancelled,
  StepCompleted,
  StepCreated,
  StepPost,
  StepReceived,
} from 'pages';
import { AuthProvider, MenuProvider } from 'providers';
import { ResponseInterceptor } from 'services';
import { GlobalStyles } from 'styles';
import { Store } from 'types';

function AdminDashboardRedirect() {
  const { user } = useAuth();
  const { isAdmin } = user;

  if (isAdmin) {
    return <Navigate to={routes.returnsAndRefunds.path} replace />;
  }

  return <DashboardPage />;
}

const {
  batchCollection,
  confirmCollect,
  dashboard,
  exchangeReturnForm,
  financialTransactions,
  login,
  ordersCodeList,
  performance,
  pixTransactionsReport,
  profile,
  resetPassword,
  exchangeAndReturn,
  scoreReport,
  pickingManagement,
  regulatoryInformation,
  discount,
  openingDays,
} = routes;

const {
  details,
  selectReturn,
  stepCreated,
  stepReceived,
  stepPost,
  stepCompleted,
  stepCancelled,
} = outletRoutes;

function RequireAuth({
  redirectPath = login.path,
  children,
}: {
  redirectPath?: string;
  children: JSX.Element;
}) {
  const {
    signed,
    selectedStore,
    stores: storesByBrand,
    setSelectedStore,
  } = useAuth();
  const location = useLocation();

  if (!signed) {
    return <Navigate to={redirectPath} state={{ from: location }} />;
  }

  const { search } = window.location;
  const params = new URLSearchParams(search);
  const newStore = params.get('store');

  if (newStore && newStore !== selectedStore.name) {
    const storeByBrand = storesByBrand.find(storeByBrand => {
      const { stores } = storeByBrand;

      return stores.some(store => store === newStore);
    });

    if (storeByBrand) {
      const { access, brand } = storeByBrand;

      const newSelectedStore: Store = {
        access,
        brand,
        name: newStore,
      };

      setLocalStorageItem(LocalStorageKeys.SelectedStore, newSelectedStore);

      setSelectedStore(newSelectedStore);
    }
  }

  return children;
}

function AppWrapper() {
  useClarity();
  loadFont({ google: { families: ['Montserrat:300,400,500,600,700,900'] } });
  const [theme, updateTheme] = useState(light);

  const handleSwitchTheme = () => {
    updateTheme(oldTheme => (oldTheme.theme === 'dark' ? light : dark));
  };
  return (
    <GoogleReCaptchaProvider reCaptchaKey={CAPTCHA_KEY}>
      <AuthProvider>
        <ThemeContext.Provider value={{ switchTheme: handleSwitchTheme }}>
          <ThemeProvider theme={theme}>
            <GlobalStyles />

            <ResponseInterceptor />

            <MenuProvider>
              <Outlet />
            </MenuProvider>
          </ThemeProvider>
        </ThemeContext.Provider>
      </AuthProvider>

      <ToastContainer
        theme="colored"
        closeOnClick
        limit={1}
        position={toast.POSITION.BOTTOM_RIGHT}
      />
    </GoogleReCaptchaProvider>
  );
}

const routesConfig = createBrowserRouter([
  {
    element: <AppWrapper />,
    children: [
      {
        path: dashboard.path,
        element: (
          <RequireAuth>
            <AdminDashboardRedirect />
          </RequireAuth>
        ),
      },
      {
        path: performance.path,
        element: (
          <RequireAuth>
            <PerformancePage />
          </RequireAuth>
        ),
      },
      {
        path: financialTransactions.path,
        element: (
          <RequireAuth>
            <FinancialTransactionsPage />
          </RequireAuth>
        ),
      },
      {
        path: profile.path,
        element: (
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        ),
      },
      {
        path: openingDays.path,
        element: (
          <RequireAuth>
            <OpeningDaysPage />
          </RequireAuth>
        ),
      },
      {
        path: discount.path,
        element: (
          <RequireAuth>
            <DiscountsPage />
          </RequireAuth>
        ),
      },
      {
        element: (
          <RequireAuth>
            <BatchCollectionPage />
          </RequireAuth>
        ),
        children: [
          {
            path: batchCollection.path,
            element: <SelectConsignmentsOutlet />,
          },
          {
            path: confirmCollect.path,
            element: <ConfirmCollectOutlet />,
          },
        ],
      },
      {
        path: exchangeAndReturn.path,
        element: (
          <RequireAuth>
            <ExchangeAndReturnPage />
          </RequireAuth>
        ),
        children: [
          {
            path: '',
            element: <NewExchange />,
          },
          {
            path: selectReturn.path,
            element: <ConsignmentDetails />,
          },
          {
            path: details.path,
            element: <ReturnDetails />,
            children: [
              {
                path: stepCreated.path,
                element: <StepCreated />,
              },
              {
                path: stepPost.path,
                element: <StepPost />,
              },
              {
                path: stepReceived.path,
                element: <StepReceived />,
              },
              {
                path: stepCompleted.path,
                element: <StepCompleted />,
              },
              {
                path: stepCancelled.path,
                element: <StepCancelled />,
              },
            ],
          },
        ],
      },
      {
        path: exchangeReturnForm.path,
        element: (
          <RequireAuth>
            <ExchangeReturnForm />
          </RequireAuth>
        ),
      },
      {
        path: scoreReport.path,
        element: (
          <RequireAuth>
            <ScoreReportPage />
          </RequireAuth>
        ),
      },
      {
        path: pickingManagement.path,
        element: (
          <RequireAuth>
            <PickingManagement />
          </RequireAuth>
        ),
      },
      {
        path: regulatoryInformation.path,
        element: (
          <RequireAuth>
            <RegulatoryInformationPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.returnsAndRefunds.path,
        element: (
          <RequireAuth>
            <ReturnsAndRefundsPage />
          </RequireAuth>
        ),
      },
      {
        path: pixTransactionsReport.path,
        element: (
          <RequireAuth>
            <PixTransactionsReportPage />
          </RequireAuth>
        ),
      },
      {
        path: '/devolucoes-e-estornos/:searchKey',
        element: (
          <RequireAuth>
            <ReturnsAndRefundsDetails />
          </RequireAuth>
        ),
      },
      {
        path: ordersCodeList.path,
        element: <OrdersCodeListPage />,
      },
      {
        path: login.path,
        element: <LoginPage />,
      },
      {
        path: resetPassword.path,
        element: <ResetPasswordPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

export const router = routesConfig;
